/**
 * ui slice: UI 알림, 모달 등 공통 UI 상태 관리
 * wehago-conference의 redux/modules/alert + 기타 UI 상태 통합
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { VideoQualityOverride } from '@/conference/config/videoQualityPolicy';
import type { AlertType, Alert, RoomStatus } from '@/types/store';

// re-export for backwards compatibility
export type { AlertType, Alert, RoomStatus } from '@/types/store';

export interface UIState {
  roomStatus: RoomStatus;
  alerts: Alert[];
  // 각종 모달 열림 상태
  isSettingsModalOpen: boolean;
  isParticipantListOpen: boolean;
  isDeviceSettingOpen: boolean;
  isVirtualBackgroundOpen: boolean;
  isDrawingOpen: boolean;
  isDocumentShareOpen: boolean;
  isVoiceNoteOpen: boolean;
  isCaptionEnabled: boolean;
  // 연결 끊김 모달
  isConnectionDropped: boolean;
  // 전체 화면 모드
  isFullscreen: boolean;
  // 화면 레이아웃 모드: 'split' = 분할화면(splitmod_N), 'focus' = 발언자 집중 (/video 라우트용)
  viewMode: 'split' | 'focus';
  // 화면 레이아웃 모드: GRID/SIDE/PRESENTER (/video-renewal 라우트용)
  screenMode: 'GRID' | 'SIDE' | 'PRESENTER';
  // 사이드바 콘텐츠: 'participants' = 참여자, 'chat' = 채팅, 'speech' = 음성텍스트, 'invite' = 초대, 'translation' = 자막/번역
  sidebarContent: 'participants' | 'chat' | 'speech' | 'invite' | 'translation';
  // 사이드바 열림 상태 (/video-renewal 라우트용)
  openSideBar: boolean;
  // 번역 모드 (/video-renewal 라우트용)
  isTranslationMode: boolean;
  // 그리드 정렬: AUTO(입장순) / SPEAKER(최근발언순) / NAME(이름순)
  screenSort: 'AUTO' | 'SPEAKER' | 'NAME';
  // 그리드 최대 표시 수 (페이지네이션 기준)
  maxGridSize: number;
  // 그리드 현재 페이지 (1-based)
  gridPage: number;
  // 화면공유 클릭 여부 (PRESENTER 모드에서 공유자 우선표시)
  isShareClick: boolean;
  // 화면 자동 전환 ON/OFF (발표자 1명 + 고정 없음일 때만 유효, 기본값 ON)
  isAutoScreenSwitch: boolean;
  // PRESENTER 모드에서 더블클릭으로 강제 지정된 참여자 ID (고정 화면 대신 표시 중)
  presenterOverrideUserId: string | null;
  // 테스트 탭에서 수동 설정한 비디오 수신 해상도 오버라이드 (null = 정책 기본값 사용)
  videoQualityOverride: VideoQualityOverride | null;
  // 초대된 참석자 목록 재조회 트리거 (값이 변경되면 ParticipantList에서 재조회)
  invitedParticipantVersion: number;
}

const initialState: UIState = {
  roomStatus: 'INIT',
  alerts: [],
  isSettingsModalOpen: false,
  isParticipantListOpen: false,
  isDeviceSettingOpen: false,
  isVirtualBackgroundOpen: false,
  isDrawingOpen: false,
  isDocumentShareOpen: false,
  isVoiceNoteOpen: false,
  isCaptionEnabled: false,
  isConnectionDropped: false,
  isFullscreen: false,
  viewMode: 'split',
  screenMode: 'GRID',
  sidebarContent: 'participants',
  openSideBar: false,
  isTranslationMode: false,
  screenSort: 'AUTO',
  maxGridSize: 16,
  gridPage: 1,
  isShareClick: false,
  isAutoScreenSwitch: true,
  presenterOverrideUserId: null,
  videoQualityOverride: null,
  invitedParticipantVersion: 0,
};

let alertIdCounter = 0;

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setRoomStatus(state, action: PayloadAction<RoomStatus>) {
      state.roomStatus = action.payload;
    },

    showAlert(state, action: PayloadAction<Omit<Alert, 'id'>>) {
      alertIdCounter += 1;
      state.alerts.push({
        ...action.payload,
        id: String(alertIdCounter),
      });
    },

    dismissAlert(state, action: PayloadAction<string>) {
      state.alerts = state.alerts.filter((a) => a.id !== action.payload);
    },

    clearAlerts(state) {
      state.alerts = [];
    },

    setSettingsModalOpen(state, action: PayloadAction<boolean>) {
      state.isSettingsModalOpen = action.payload;
    },

    setParticipantListOpen(state, action: PayloadAction<boolean>) {
      state.isParticipantListOpen = action.payload;
    },

    setDeviceSettingOpen(state, action: PayloadAction<boolean>) {
      state.isDeviceSettingOpen = action.payload;
    },

    setVirtualBackgroundOpen(state, action: PayloadAction<boolean>) {
      state.isVirtualBackgroundOpen = action.payload;
    },

    setDrawingOpen(state, action: PayloadAction<boolean>) {
      state.isDrawingOpen = action.payload;
    },

    setDocumentShareOpen(state, action: PayloadAction<boolean>) {
      state.isDocumentShareOpen = action.payload;
    },

    setVoiceNoteOpen(state, action: PayloadAction<boolean>) {
      state.isVoiceNoteOpen = action.payload;
    },

    setCaptionEnabled(state, action: PayloadAction<boolean>) {
      state.isCaptionEnabled = action.payload;
    },

    setConnectionDropped(state, action: PayloadAction<boolean>) {
      state.isConnectionDropped = action.payload;
    },

    setFullscreen(state, action: PayloadAction<boolean>) {
      state.isFullscreen = action.payload;
    },

    setViewMode(state, action: PayloadAction<'split' | 'focus'>) {
      state.viewMode = action.payload;
    },

    setSidebarContent(state, action: PayloadAction<'participants' | 'chat' | 'speech' | 'invite' | 'translation'>) {
      state.sidebarContent = action.payload;
      state.openSideBar = true;
    },

    setScreenMode(state, action: PayloadAction<'GRID' | 'SIDE' | 'PRESENTER'>) {
      state.screenMode = action.payload;
    },

    setOpenSideBar(state, action: PayloadAction<boolean>) {
      state.openSideBar = action.payload;
    },

    toggleSideBar(state) {
      state.openSideBar = !state.openSideBar;
    },

    setTranslationMode(state, action: PayloadAction<boolean>) {
      state.isTranslationMode = action.payload;
    },

    setScreenSort(state, action: PayloadAction<'AUTO' | 'SPEAKER' | 'NAME'>) {
      state.screenSort = action.payload;
    },

    setMaxGridSize(state, action: PayloadAction<number>) {
      state.maxGridSize = action.payload;
    },

    setGridPage(state, action: PayloadAction<number>) {
      state.gridPage = Math.max(1, action.payload);
    },

    setIsShareClick(state, action: PayloadAction<boolean>) {
      state.isShareClick = action.payload;
    },

    setAutoScreenSwitch(state, action: PayloadAction<boolean>) {
      state.isAutoScreenSwitch = action.payload;
    },

    setPresenterOverrideUserId(state, action: PayloadAction<string | null>) {
      state.presenterOverrideUserId = action.payload;
    },

    setVideoQualityOverride(state, action: PayloadAction<VideoQualityOverride>) {
      state.videoQualityOverride = action.payload;
    },

    clearVideoQualityOverride(state) {
      state.videoQualityOverride = null;
    },

    /** 초대된 참석자 목록 재조회 트리거 */
    bumpInvitedParticipantVersion(state) {
      state.invitedParticipantVersion += 1;
    },

    reset() {
      return initialState;
    },
  },
});

export const uiActions = uiSlice.actions;
export default uiSlice.reducer;
